import { BaseQueryFn } from '@reduxjs/toolkit/query';
import axios, { AxiosRequestConfig } from 'axios';
import {
    apiUrl,
    neoApiUrl,
    neoRequestTimeoutMs,
    ACCESS_TOKEN,
    USER_DATA_STORAGE_ID,
    NEO_ACCESSES_STORAGE_ID,
    NEO_SERVER_STORAGE_ID,
    NEO_SERVER_ACCESS_STORAGE_ID,
    NEO_USER_EMAIL_STORAGE_ID,
} from '../../config';
import { INeoUserAccessAttributes, INeoHeaders, IUserLSInfo } from '../../state/neo/types';

const rtkAxios = axios.create({
    baseURL: apiUrl,
});

export const neoRtkAxios = axios.create({
    baseURL: neoApiUrl,
    timeout: neoRequestTimeoutMs,
});
type NeoServerDescriptor = {
    id: string;
    url?: string;
    path?: string;
};

type StoredNeoServer = {
    id: string;
    url: string;
};

const stripTrailingSlashes = (value: string): string => String(value ?? '').replace(/\/+$/, '');

/**
 * The backend knows the NEO host in most deployments and sends an absolute
 * `url`. Where it does not (NEO configured only on the front end), it sends a
 * `path` to resolve against REACT_APP_NEO_HTTP_API_URL — which is how the three
 * built-in targets ('', '/instinct', '/mfg') behaved before the registry.
 */
export const resolveNeoServerUrl = (server: NeoServerDescriptor): string => {
    const absolute = stripTrailingSlashes(server.url ?? '');

    if (absolute) {
        return absolute;
    }

    return `${stripTrailingSlashes(neoApiUrl)}${server.path ?? ''}`;
};

export const getStoredNeoServer = (): StoredNeoServer | null => {
    try {
        const raw = localStorage.getItem(NEO_SERVER_STORAGE_ID);

        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw) as StoredNeoServer;

        return parsed && parsed.id ? parsed : null;
    } catch {
        // A malformed entry should cost the stored preference, nothing more.
        return null;
    }
};

/** Point every NEO query at `server` and remember the choice across reloads. */
export const setNeoServer = (server: NeoServerDescriptor): string => {
    const url = resolveNeoServerUrl(server);

    neoRtkAxios.defaults.baseURL = url;

    try {
        localStorage.setItem(NEO_SERVER_STORAGE_ID, JSON.stringify({ id: server.id, url }));
    } catch {
        // Private-mode or quota failures must not break the switch itself.
    }

    return url;
};

type NeoServerAccess = {
    groups: Array<{ groupName: string; token: string; adminToken?: string }>;
    teams: string[];
};

/** Per-server access map, mirroring usermeta.meta_key = 'neo'. */
export const getStoredNeoAccessMap = (): Record<string, NeoServerAccess> => {
    try {
        const raw = localStorage.getItem(NEO_SERVER_ACCESS_STORAGE_ID);

        return raw ? (JSON.parse(raw) as Record<string, NeoServerAccess>) : {};
    } catch {
        return {};
    }
};

export const setStoredNeoServerAccess = (serverId: string, access: NeoServerAccess): void => {
    try {
        const map = getStoredNeoAccessMap();

        map[serverId] = access;

        localStorage.setItem(NEO_SERVER_ACCESS_STORAGE_ID, JSON.stringify(map));
    } catch {
        // Cache only — the backend copy in usermeta is the durable one.
    }
};

/**
 * Groups for one server — `serverId` when given, otherwise the selected one.
 * Null when none are cached; callers then fall back to the legacy flat
 * Cognito-derived list, which is correct for the default server and is what
 * every existing user has.
 */
export const getNeoAccessGroups = (serverId?: string): NeoServerAccess['groups'] | null => {
    const id = serverId ?? getStoredNeoServer()?.id;

    if (!id) {
        return null;
    }

    const access = getStoredNeoAccessMap()[id];

    return access?.groups?.length ? access.groups : null;
};

// Applied at module load, before the registry has been fetched, so a reload
// keeps talking to the selected server instead of flashing back to the default.
const storedNeoServer = getStoredNeoServer();

if (storedNeoServer?.url) {
    neoRtkAxios.defaults.baseURL = storedNeoServer.url;
}

// The subset of an axios rejection this handler reads. Deliberately NOT
// `extends AxiosError`: `status` only moved onto AxiosError in recent axios
// releases, so extending it pins the file to one version of the dependency
// (redeclaring `status` => TS2430 on new axios, inheriting it => TS2339 on old).
interface ClientError {
    message?: string;
    /** axios failure code — set (with no `response`) when nothing came back at all. */
    code?: string;
    status?: number | string;
    response?: {
        status?: number;
        data?: unknown;
    };
    data?: {
        message?: string;
        status?: number | string;
    };
}

// axios codes that mean "the request never reached a server, or the server
// never answered" — as opposed to an HTTP error, which carries a response.
const UNREACHABLE_CODES = ['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK'];

type ErrorBody = { message?: string; error?: string };

const readErrorBody = (body: ErrorBody | null | undefined): string | undefined =>
    body?.message || body?.error || undefined;

/**
 * Pull the server's own message out of an error response.
 *
 * Downloads are requested with `responseType: 'arraybuffer'` / `'blob'`, and axios
 * applies that type to error responses too — so the JSON body the API sends with a
 * 4xx arrives as binary and never gets parsed. Without decoding it here the UI falls
 * back to axios's generic "Request failed with status code 404" and hides messages
 * like "Run date exceeds retention policy, this report is deleted".
 */
const extractErrorMessage = async (data: unknown): Promise<string | undefined> => {
    if (!data) {
        return undefined;
    }

    let text: string | undefined;

    if (typeof data === 'string') {
        text = data;
    } else if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
        text = new TextDecoder().decode(data as BufferSource);
    } else if (typeof Blob !== 'undefined' && data instanceof Blob) {
        text = await data.text();
    } else {
        // Already parsed by axios — the normal JSON-response case.
        return readErrorBody(data as ErrorBody);
    }

    try {
        return text ? readErrorBody(JSON.parse(text) as ErrorBody) : undefined;
    } catch {
        // Not JSON (an HTML error page, a proxy message) — nothing usable for the user.
        return undefined;
    }
};

export const axiosBaseQuery =
    (
        apiHost?: string
    ): BaseQueryFn<
        {
            url: string;
            method: AxiosRequestConfig['method'];
            data?: AxiosRequestConfig['data'];
            params?: AxiosRequestConfig['params'];
            headers?: AxiosRequestConfig['headers'];
            responseType?: AxiosRequestConfig['responseType'];
            /**
             * Send this one NEO request to a specific server instead of the
             * selected one, using that server's own access groups. Used by
             * admin screens that read or grant access on a server the admin is
             * not currently working in — switching the global selection just to
             * add a user would drop every cached response and move the whole UI.
             */
            neoServer?: { id: string; url: string };
            /**
             * Send this NEO request without a Neo-Access-Groups header.
             *
             * For bootstrap calls, which run precisely when no groups are known
             * for the target server. Without this they inherit the legacy flat
             * Cognito list — tokens another NEO minted — and the server refuses
             * the whole request ("Some of your access tokens are invalid"), so
             * the call that was meant to obtain valid groups can never succeed.
             */
            neoSkipAccessGroups?: boolean;
        },
        unknown,
        unknown
    > =>
    async ({ url, method, data, params, headers, responseType, neoServer, neoSkipAccessGroups }) => {
        try {
            let currentApiClient = rtkAxios;
            const accessToken = localStorage.getItem(ACCESS_TOKEN);

            if (apiHost === 'neo') {
                currentApiClient = neoRtkAxios;

                const userData = JSON.parse(localStorage.getItem(USER_DATA_STORAGE_ID)!) as IUserLSInfo;
                const neoAccesses = localStorage.getItem(NEO_ACCESSES_STORAGE_ID);

                // Prefer the address the API resolved for this user over the
                // one in userData: NEO is keyed on the former, and the OAuth
                // userinfo response can carry a different address entirely.
                const neoUserEmail = localStorage.getItem(NEO_USER_EMAIL_STORAGE_ID) || userData.email;

                const neoHeaders: INeoHeaders = { 'User-Email': neoUserEmail };

                // Access-group tokens are issued per NEO server. Sending the
                // whole list makes the selected server reject every token it did
                // not mint ("Some of your access tokens are invalid"), so send
                // only the groups belonging to the server being addressed.
                const serverGroups = neoSkipAccessGroups ? null : getNeoAccessGroups(neoServer?.id);

                if (serverGroups) {
                    neoHeaders['Neo-Access-Groups'] = JSON.stringify(serverGroups);
                } else if (neoAccesses && !neoSkipAccessGroups) {
                    const parsedAccesses = JSON.parse(neoAccesses) as INeoUserAccessAttributes;

                    neoHeaders['Neo-Access-Groups'] = JSON.stringify(parsedAccesses.accessGroups);
                }

                headers = { ...headers, ...neoHeaders };
            }

            const result = await currentApiClient({
                url,
                method,
                data,
                params,
                headers: { ...headers, Authorization: accessToken },
                responseType,
                // Spread so the key is absent, not undefined, when no override
                // is given — the instance's own baseURL then applies.
                ...(apiHost === 'neo' && neoServer?.url ? { baseURL: neoServer.url } : {}),
            });

            return { data: result.data };
        } catch (axiosError) {
            const err = axiosError as ClientError;

            // No response at all: the selected NEO is down, blocked, or too slow.
            // axios's own text ("timeout of 30000ms exceeded", "Network Error")
            // does not say which server, which is the one thing worth knowing
            // when several are selectable.
            if (apiHost === 'neo' && !err.response && UNREACHABLE_CODES.includes(err.code ?? '')) {
                const target = getStoredNeoServer();

                return {
                    error: {
                        status: err.code === 'ERR_NETWORK' ? 'NETWORK' : 'TIMEOUT',
                        data: `NEO server "${target?.id ?? 'default'}" (${
                            target?.url || neoApiUrl
                        }) did not respond. It may be unavailable — try another server.`,
                    },
                };
            }

            const serverMessage = await extractErrorMessage(err.response?.data);

            return {
                error: {
                    status: err.response?.status || err.status || err.data?.status,
                    data: serverMessage || err.message || err.data?.message,
                },
            };
        }
    };
